import React, { useState } from 'react';
import { 
  XMarkIcon, 
  CheckIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import Badge from '../common/Badge';
import toast from 'react-hot-toast';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'pro'>('basic');
  const [loading, setLoading] = useState(false);
  const { user, updateSubscription } = useAuth();

  const plans = {
    free: {
      name: 'Free',
      price: 0,
      description: '기본 프롬프트 사용',
      features: [
        '월 10개 프롬프트 사용',
        '기본 카테고리 접근',
        '커뮤니티 지원',
      ],
      limitations: [
        '개인화 기능 제한',
        'AI 통합 기능 없음',
        '우선 지원 없음',
      ]
    },
    basic: {
      name: 'Basic',
      price: 9900,
      description: '개인 사용자를 위한 완벽한 솔루션',
      features: [
        '무제한 프롬프트 사용',
        '모든 카테고리 접근',
        '프롬프트 개인화 기능',
        '즐겨찾기 및 히스토리',
        '이메일 지원',
      ],
      popular: false,
    },
    pro: {
      name: 'Pro',
      price: 19900,
      description: '비즈니스 사용자를 위한 고급 기능',
      features: [
        'Basic의 모든 기능',
        'AI 통합 기능 (ChatGPT 연동)',
        '맞춤형 프롬프트 생성',
        '팀 협업 기능',
        '우선 지원',
        '월간 리포트',
        'API 접근권한',
      ],
      popular: true,
    },
  };

  const handleUpgrade = async () => {
    if (!user) {
      toast.error('로그인이 필요합니다.');
      return;
    }

    setLoading(true);
    try {
      // 실제 결제 처리 대신 목업
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      updateSubscription(selectedPlan);
      toast.success(`${plans[selectedPlan].name} 플랜으로 업그레이드되었습니다!`);
      onClose();
    } catch (error) {
      toast.error('업그레이드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* 배경 오버레이 */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* 모달 컨텐츠 */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* 헤더 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                요금제 선택
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                목표에 맞는 플랜을 선택하여 더 많은 기능을 이용해보세요
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* 요금제 카드들 */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Free Plan */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plans.free.name}
                  </h3>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-gray-900">₩0</span>
                    <span className="text-gray-500">/월</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {plans.free.description}
                  </p>
                </div>

                <ul className="mt-6 space-y-3">
                  {plans.free.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant="outline"
                  fullWidth
                  className="mt-6"
                  disabled
                >
                  현재 플랜
                </Button>
              </div>

              {/* Basic Plan */}
              <div 
                className={`border rounded-lg p-6 cursor-pointer transition-all ${
                  selectedPlan === 'basic'
                    ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                onClick={() => setSelectedPlan('basic')}
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plans.basic.name}
                  </h3>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-gray-900">
                      ₩{plans.basic.price.toLocaleString()}
                    </span>
                    <span className="text-gray-500">/월</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {plans.basic.description}
                  </p>
                </div>

                <ul className="mt-6 space-y-3">
                  {plans.basic.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pro Plan */}
              <div 
                className={`border rounded-lg p-6 cursor-pointer transition-all relative ${
                  selectedPlan === 'pro'
                    ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                onClick={() => setSelectedPlan('pro')}
              >
                {plans.pro.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge variant="primary" className="flex items-center">
                      <StarIcon className="w-3 h-3 mr-1" />
                      인기
                    </Badge>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plans.pro.name}
                  </h3>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-gray-900">
                      ₩{plans.pro.price.toLocaleString()}
                    </span>
                    <span className="text-gray-500">/월</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {plans.pro.description}
                  </p>
                </div>

                <ul className="mt-6 space-y-3">
                  {plans.pro.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 결제 섹션 */}
            <div className="mt-8 text-center">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  선택된 플랜: {plans[selectedPlan].name}
                </h4>
                
                <div className="text-3xl font-bold text-primary-600 mb-2">
                  ₩{plans[selectedPlan].price.toLocaleString()}
                  <span className="text-base font-normal text-gray-500">/월</span>
                </div>
                
                <p className="text-sm text-gray-600 mb-6">
                  언제든지 취소 가능 • 7일 무료 체험
                </p>

                <div className="flex items-center justify-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={loading}
                  >
                    나중에
                  </Button>
                  
                  <Button
                    variant="primary"
                    onClick={handleUpgrade}
                    loading={loading}
                    className="px-8"
                  >
                    지금 시작하기
                  </Button>
                </div>
              </div>
            </div>

            {/* 보안 및 보장 */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <p>🔒 안전한 결제 • 30일 환불 보장 • 24/7 고객 지원</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;